import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { getShiftListTimesheetStatus } from "../../UpdatedTimeSheet/TimeSheetDetailsSection/TabSection/NewCodeAllocation/v5BillingUtils";
import "./timesheet-status-cell.css";

const TOOLTIP_ESTIMATED_HEIGHT = 160;
const TOOLTIP_GAP = 8;

const getWorkerToneClass = (badgeClass) => {
  if (badgeClass === "bs-badge--approved") return "is-approved";
  if (
    badgeClass === "bs-badge--rejected" ||
    badgeClass === "bs-badge--expired"
  ) {
    return "is-rejected";
  }
  return "";
};

const TimesheetStatusCell = ({ item, tdClassName = "" }) => {
  const triggerRef = useRef(null);
  const [placeAbove, setPlaceAbove] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const timesheetStatus = useMemo(
    () => getShiftListTimesheetStatus(item),
    [item]
  );

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const tableRoot = el.closest(".iw-table-card, .bs-table-card");
    const footer = tableRoot?.querySelector(
      ".iw-table-footer, .bs-table-footer"
    );
    const header = el.closest("table")?.querySelector("thead");
    const footerTop = footer?.getBoundingClientRect().top ?? window.innerHeight;
    const headerBottom = header?.getBoundingClientRect().bottom ?? 0;
    const spaceBelow = footerTop - rect.bottom;
    const spaceAbove = rect.top - headerBottom;
    const shouldPlaceAbove =
      spaceBelow < TOOLTIP_ESTIMATED_HEIGHT && spaceAbove > spaceBelow;

    setPlaceAbove(shouldPlaceAbove);
    setCoords({
      left: rect.left + rect.width / 2,
      top: shouldPlaceAbove ? rect.top - TOOLTIP_GAP : rect.bottom + TOOLTIP_GAP,
    });
  }, []);

  const openTooltip = useCallback(() => {
    updatePosition();
    setIsOpen(true);
  }, [updatePosition]);

  const closeTooltip = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onReposition = () => updatePosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [isOpen, updatePosition]);

  if (timesheetStatus.label === "—") {
    return (
      <td className={`bs-td-ts-status ${tdClassName}`.trim()}>
        <span className="bs-ts-status-empty">—</span>
      </td>
    );
  }

  const tooltip =
    isOpen && coords && timesheetStatus.workers.length > 0
      ? createPortal(
          <div
            className={`bs-ts-status-tooltip is-portal${
              placeAbove ? " is-above" : ""
            }`}
            role="tooltip"
            style={{
              left: coords.left,
              top: placeAbove ? "auto" : coords.top,
              bottom: placeAbove ? window.innerHeight - coords.top : "auto",
            }}
          >
            <div className="bs-ts-status-tooltip-title">
              Worker timesheet status
            </div>
            <ul className="bs-ts-status-tooltip-list">
              {timesheetStatus.workers.map((worker, index) => (
                <li
                  key={
                    worker.timesheetUuid ??
                    worker.workerId ??
                    `${worker.name}-${index}`
                  }
                >
                  <span className="bs-ts-status-tooltip-name">
                    {worker.name}
                  </span>
                  <span
                    className={`bs-ts-status-tooltip-value ${getWorkerToneClass(
                      worker.badgeClass
                    )}`}
                  >
                    {worker.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )
      : null;

  return (
    <td className={`bs-td-ts-status ${tdClassName}`.trim()}>
      <div
        ref={triggerRef}
        className={`bs-ts-status${isOpen ? " is-open" : ""}`}
        tabIndex={timesheetStatus.workers.length > 0 ? 0 : undefined}
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltip}
        onFocus={openTooltip}
        onBlur={closeTooltip}
        aria-label={
          timesheetStatus.isPartial
            ? `Partial timesheet status. ${timesheetStatus.workers
                .map((worker) => `${worker.name}: ${worker.label}`)
                .join("; ")}`
            : `Timesheet status ${timesheetStatus.label}`
        }
      >
        <span className={`bs-badge ${timesheetStatus.badgeClass}`}>
          {timesheetStatus.label}
          {timesheetStatus.isPartial && (
            <HiOutlineInformationCircle
              className="bs-ts-status-icon"
              aria-hidden
            />
          )}
        </span>
        {tooltip}
      </div>
    </td>
  );
};

export default TimesheetStatusCell;
